/*
  Backend-coupled side of the FCM device-token lifecycle: reads the token through
  src/utils/fcm-token and registers it against our API. Anything that must stay importable from
  src/utils/token.js belongs in src/utils/fcm-token.js instead -- see the dependency note there.
*/
import { touchToken } from "src/api/notification";
import { getMessagingDeviceToken } from "src/utils/fcm-token";

// Web is DeviceType.Web in EnterpriseManagement.Domain.Entities.Users.Enums.
const WEB_DEVICE_TYPE = 1;

export async function requestNotificationsPermissions() {
  if (typeof Notification === "undefined") return;

  const permission = await Notification.requestPermission();

  if (permission === "granted") {
    await saveMessagingDeviceToken();
  }
}

/*
  Registers this device against the backend and refreshes its last_seen heartbeat. This runs on
  every app start, which is the point: the backend expires device tokens that stop reporting in, so
  a token that only announced itself once (Firebase fires onNewToken / onRegistered solely when the
  token *changes*) would eventually be pruned and stop receiving notifications.

  `registration` stays optional: requestNotificationsPermissions() above calls this with none, and
  getMessagingDeviceToken() then falls back to the service worker already registered for "/".
*/
export async function saveMessagingDeviceToken(registration) {
  try {
    const fcmToken = await getMessagingDeviceToken(registration);

    if (fcmToken) {
      await touchToken({
        token: fcmToken,
        deviceType: WEB_DEVICE_TYPE,
      });

      return;
    }

    /*
      getMessagingDeviceToken() deliberately never prompts, so the one case worth retrying is a
      permission the user has not answered yet. Guarding on "default" is what keeps the
      saveMessagingDeviceToken <-> requestNotificationsPermissions pair terminating: after
      requestPermission() resolves the permission is granted or denied, never "default" again.
    */
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      await requestNotificationsPermissions();
    }
  } catch (error) {
    console.log(error);
  }
}
